import React, { useState, useEffect } from 'react'
import { Search, User, Plus, Check } from 'lucide-react'
import { makeApiCall } from '../utils/customerUtils'
import ModalShell from './ui/ModalShell'
import ModalFooter from './ui/ModalFooter'
import { buttonVariants } from './ui/buttonVariants'

interface Customer {
  id: number
  name: string
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
}

interface SaveScenariosModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (customerId: number, title: string) => void
  isLoading: boolean
}

const EMPTY_NEW_CUSTOMER = { first_name: '', last_name: '', email: '', phone: '' }

const SaveScenariosModal: React.FC<SaveScenariosModalProps> = ({ isOpen, onClose, onSave, isLoading }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [customers, setCustomers] = useState<Customer[]>([])
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [title, setTitle] = useState('')
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false)
  const [newCustomer, setNewCustomer] = useState(EMPTY_NEW_CUSTOMER)
  const [loadingCustomers, setLoadingCustomers] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadCustomers()
      setTitle(`Mortgage Comparison - ${new Date().toLocaleDateString()}`)
    }
  }, [isOpen])

  useEffect(() => {
    const q = searchTerm.toLowerCase()
    setFilteredCustomers(
      q
        ? customers.filter(c =>
            c.name.toLowerCase().includes(q) ||
            c.email?.toLowerCase().includes(q) ||
            c.first_name?.toLowerCase().includes(q) ||
            c.last_name?.toLowerCase().includes(q)
          )
        : customers
    )
  }, [searchTerm, customers])

  const loadCustomers = async () => {
    try {
      setLoadingCustomers(true)
      const response = await makeApiCall('/api/customers')
      const data = await response.json()
      if (data.customers) {
        setCustomers(data.customers)
        setFilteredCustomers(data.customers)
      }
    } catch (error) {
      console.error('Failed to load customers:', error)
    } finally {
      setLoadingCustomers(false)
    }
  }

  const handleCreateCustomer = async () => {
    try {
      const response = await makeApiCall('/api/customers', {
        method: 'POST',
        body: JSON.stringify(newCustomer),
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await response.json()
      if (data.customer) {
        setCustomers(prev => [data.customer, ...prev])
        setSelectedCustomer(data.customer)
        setShowNewCustomerForm(false)
        setNewCustomer(EMPTY_NEW_CUSTOMER)
      }
    } catch (error) {
      console.error('Failed to create customer:', error)
    }
  }

  const handleClose = () => {
    setSearchTerm('')
    setSelectedCustomer(null)
    setTitle('')
    setShowNewCustomerForm(false)
    setNewCustomer(EMPTY_NEW_CUSTOMER)
    onClose()
  }

  return (
    <ModalShell isOpen={isOpen} onClose={handleClose} title="Save Mortgage Scenarios" size="lg">
      <div className="space-y-5">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Scenario Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g., Mortgage Comparison - January 2025"
          />
        </div>

        {/* Customer selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Select Customer</label>
            <button
              onClick={() => setShowNewCustomerForm(v => !v)}
              className="flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              <Plus className="h-3.5 w-3.5" /> New Customer
            </button>
          </div>

          {/* New customer form */}
          {showNewCustomerForm && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-3 space-y-3">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Create New Customer</p>
              <div className="grid grid-cols-2 gap-2">
                {(['first_name', 'last_name'] as const).map(field => (
                  <input
                    key={field}
                    type="text"
                    placeholder={field === 'first_name' ? 'First Name' : 'Last Name'}
                    value={newCustomer[field]}
                    onChange={(e) => setNewCustomer(prev => ({ ...prev, [field]: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                ))}
                <input type="email" placeholder="Email" value={newCustomer.email}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                <input type="tel" placeholder="Phone" value={newCustomer.phone}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <button
                onClick={handleCreateCustomer}
                disabled={!newCustomer.first_name || !newCustomer.last_name}
                className={buttonVariants.primary}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Create Customer
              </button>
            </div>
          )}

          {/* Search */}
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* List */}
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg max-h-56 overflow-y-auto">
            {loadingCustomers ? (
              <p className="p-4 text-center text-sm text-gray-500">Loading customers…</p>
            ) : filteredCustomers.length === 0 ? (
              <p className="p-4 text-center text-sm text-gray-500">No customers found</p>
            ) : (
              filteredCustomers.map(customer => (
                <div
                  key={customer.id}
                  onClick={() => setSelectedCustomer(customer)}
                  className={`flex items-center justify-between px-3 py-2.5 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    selectedCustomer?.id === customer.id ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{customer.name}</p>
                      {customer.email && <p className="text-xs text-gray-500 truncate">{customer.email}</p>}
                    </div>
                  </div>
                  {selectedCustomer?.id === customer.id && <Check className="h-4 w-4 text-indigo-600 flex-shrink-0" />}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <ModalFooter>
        <button onClick={handleClose} className={buttonVariants.ghost}>Cancel</button>
        <button
          onClick={() => selectedCustomer && title.trim() && onSave(selectedCustomer.id, title.trim())}
          disabled={!selectedCustomer || !title.trim() || isLoading}
          className={buttonVariants.success}
        >
          {isLoading ? 'Saving…' : 'Save Scenarios'}
        </button>
      </ModalFooter>
    </ModalShell>
  )
}

export default SaveScenariosModal
